
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Check,
  ArrowRight,
  ShieldCheck,
  StepForward,
  Globe,
  User,
  FileCheck,
  LogIn,
} from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import VerificationStepper from "@/components/profile/identity-verification/VerificationStepper";
import { useAuth } from "@/contexts/auth";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";

const VerificationProcess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, isLoading, isAuthenticated } = useAuth();
  const [showStepper, setShowStepper] = useState(false);

  // Check if user is authenticated on component mount and on auth state changes
  useEffect(() => {
    // If authentication is completed (not loading) and user is not authenticated, redirect to login
    if (!isLoading && !isAuthenticated) {
      // Only redirect if trying to access the stepper
      if (location.search.includes("step=")) {
        navigate("/auth", {
          state: { from: location.pathname + location.search },
        });
      }
    }
  }, [isLoading, isAuthenticated, navigate, location]);

  // Check URL params for step information on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has("step") && isAuthenticated) {
      setShowStepper(true);
    }
  }, [location.search, isAuthenticated]);

  const steps = [
    {
      title: "Select verification country",
      description: "Choose which country you want to be verified for.",
      icon: <Globe className="h-6 w-6 text-nation-purple" />,
    },
    {
      title: "Complete your profile",
      description: "Provide your personal details and residency status.",
      icon: <User className="h-6 w-6 text-nation-purple" />,
    },
    {
      title: "Submit identification documents",
      description:
        "Upload government-issued ID documents based on your residency status.",
      icon: <FileCheck className="h-6 w-6 text-nation-purple" />,
    },
    {
      title: "Review process",
      description:
        "Our team will review your submission, typically within 2-3 business days.",
      icon: <ShieldCheck className="h-6 w-6 text-nation-purple" />,
    },
    {
      title: "Get verified",
      description:
        "Once approved, you'll receive a verification badge and access to country-specific features.",
      icon: <Check className="h-6 w-6 text-nation-purple" />,
    },
  ];

  const handleStartVerification = () => {
    if (isAuthenticated) {
      setShowStepper(true);
      // Update URL to include step parameter for persistence
      navigate("/verification-process?step=1", { replace: true });
    } else {
      navigate("/auth", { state: { from: "/verification-process?step=1" } });
    }
  };

  const handleStepperComplete = () => {
    navigate("/profile?tab=verification");
  };

  // Early return if still loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 pt-24 pb-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nation-purple mx-auto"></div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Verification Process - NationCard™</title>
        <meta
          name="description"
          content="Learn about the verification process for becoming a verified pollster on NationCard™."
        />
      </Helmet>

      <Header />

      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {!isAuthenticated && (
              <Alert className="mb-6 border-amber-200 bg-amber-50 dark:bg-amber-950/20">
                <AlertTitle className="flex items-center gap-2">
                  <LogIn className="h-5 w-5" /> Authentication Required
                </AlertTitle>
                <AlertDescription className="mt-2">
                  <p className="mb-4">
                    You need to be logged in to start the verification process.
                    Please sign in or create an account to continue.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() =>
                        navigate("/auth", { state: { from: location } })
                      }
                      variant="purple"
                    >
                      Sign In or Register
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {!showStepper ? (
              <>
                <div className="mb-10 text-center">
                  <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-gray-100">
                    Country-Specific Verification Process
                  </h1>
                  <p className="text-lg text-gray-600 dark:text-gray-300">
                    Become a verified pollster for specific countries and
                    contribute to the democratic process. Verified pollsters
                    help shape policy decisions and hold elected officials
                    accountable.
                  </p>
                </div>

                <div className="mb-12">
                  <Card className="bg-nation-purple/5 border-nation-purple/20">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                        <ShieldCheck className="h-6 w-6 text-nation-purple" />
                        Why Get Verified?
                      </CardTitle>
                      <CardDescription className="text-gray-600 dark:text-gray-300">
                        Verification ensures that polls on NationCard™ reflect
                        authentic local opinions and prevents fraud.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="border border-border bg-card rounded-md p-4">
                          <h3 className="font-semibold mb-2 text-gray-900 dark:text-gray-100">
                            Benefits of Verification
                          </h3>
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start gap-2">
                              <Check className="h-4 w-4 mt-0.5 text-green-500" />
                              <span className="text-gray-700 dark:text-gray-300">
                                Access to country-specific polls and features
                              </span>
                            </li>
                            <li className="flex items-start gap-2">
                              <Check className="h-4 w-4 mt-0.5 text-green-500" />
                              <span className="text-gray-700 dark:text-gray-300">
                                Create and submit your own poll proposals
                              </span>
                            </li>
                            <li className="flex items-start gap-2">
                              <Check className="h-4 w-4 mt-0.5 text-green-500" />
                              <span className="text-gray-700 dark:text-gray-300">
                                Your votes carry more weight in poll statistics
                              </span>
                            </li>
                          </ul>
                        </div>
                        <div className="border border-border bg-card rounded-md p-4">
                          <h3 className="font-semibold mb-2 text-gray-900 dark:text-gray-100">
                            Expatriates & Residents
                          </h3>
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-start gap-2">
                              <Check className="h-4 w-4 mt-0.5 text-green-500" />
                              <span className="text-gray-700 dark:text-gray-300">
                                Tailored verification for both residents and
                                expatriates
                              </span>
                            </li>
                            <li className="flex items-start gap-2">
                              <Check className="h-4 w-4 mt-0.5 text-green-500" />
                              <span className="text-gray-700 dark:text-gray-300">
                                Verify for multiple countries if eligible
                              </span>
                            </li>
                            <li className="flex items-start gap-2">
                              <Check className="h-4 w-4 mt-0.5 text-green-500" />
                              <span className="text-gray-700 dark:text-gray-300">
                                Different document requirements based on
                                residency status
                              </span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-gray-100">Verification Steps</h2>
                <div className="space-y-6 mb-12">
                  {steps.map((step, index) => (
                    <Card
                      key={index}
                      className="border-l-4 border-l-nation-blue"
                    >
                      <CardContent className="p-6">
                        <div className="flex gap-4">
                          <div className="h-10 w-10 rounded-full bg-nation-blue text-white flex items-center justify-center flex-shrink-0">
                            {index + 1}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                                {step.title}
                              </h3>
                              {step.icon && (
                                <span className="text-nation-purple">
                                  {step.icon}
                                </span>
                              )}
                            </div>
                            <p className="text-gray-600 dark:text-gray-300">
                              {step.description}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-10">
                  <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Privacy & Security</h2>
                  <p className="mb-4 text-gray-700 dark:text-gray-300">
                    Your identity information is securely stored and only used
                    for verification purposes. We comply with data protection
                    regulations and never share your personal documents with
                    third parties.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300">
                    See our{" "}
                    <Link
                      to="/legal/privacy-policy"
                      className="text-nation-blue hover:text-nation-blue-dark underline"
                    >
                      Privacy Policy
                    </Link>{" "}
                    for more information about how we handle your data.
                  </p>
                </div>

                <div className="flex flex-col md:flex-row gap-4 justify-center">
                  <Button
                    onClick={handleStartVerification}
                    size="lg"
                    className="bg-nation-purple hover:bg-nation-purple-dark text-white hover:text-white"
                  >
                    {isAuthenticated
                      ? "Start Verification Process"
                      : "Sign In to Start Verification"}
                    <StepForward className="ml-2 h-4 w-4" />
                  </Button>
                  {isAuthenticated && (
                    <Button asChild size="lg" variant="outline">
                      <Link to="/profile?tab=verification">
                        Go to Verification Tab
                      </Link>
                    </Button>
                  )}
                </div>
              </>
            ) : isAuthenticated ? (
              <VerificationStepper onComplete={handleStepperComplete} />
            ) : (
              <Alert variant="destructive">
                <AlertTitle>Authentication Required</AlertTitle>
                <AlertDescription>
                  Please sign in to continue with the verification process.
                  <Button
                    onClick={() =>
                      navigate("/auth", { state: { from: location } })
                    }
                    variant="outline"
                    className="mt-2"
                  >
                    Sign In or Register
                  </Button>
                </AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VerificationProcess;
